<template>
    <div class="sk-element-wrapper" :class="element.id + '-wrapper ' + element.props.class" v-if="element.props" @click.stop="onClick()">
        <div class="twpb-business-directory-preview" :class="element.props.themeStyle" :style="styleObject">
            <div v-if="showTabs" class="twpb-business-directory-tabs mb-3">
                <button
                    v-for="tab in categoryTabs"
                    :key="`tab-${tab.id}`"
                    type="button"
                    class="btn btn-sm me-2 mb-2"
                    :class="activeTab === tab.id ? 'btn-dark' : 'btn-outline-dark'"
                    @click.stop="activeTab = tab.id"
                >
                    {{ tab.name }}
                </button>
            </div>

            <template v-if="viewMode === 'table'">
                <div class="table-responsive">
                    <table class="table table-sm table-bordered mb-0">
                        <thead>
                            <tr>
                                <th v-for="column in visibleColumns" :key="`head-${column}`">{{ columnLabel(column) }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in visibleItems" :key="`row-${item.id}`">
                                <td v-for="column in visibleColumns" :key="`cell-${item.id}-${column}`">
                                    <template v-if="column === 'image'">
                                        <img
                                            v-if="imageUrl(item)"
                                            :src="imageUrl(item)"
                                            :alt="item.name || ''"
                                            style="width: 72px; height: 50px; object-fit: cover; border-radius: 4px"
                                        />
                                    </template>
                                    <template v-else-if="column === 'name'">{{ item.name || "" }}</template>
                                    <template v-else-if="column === 'email'">{{ item.email || "" }}</template>
                                    <template v-else-if="column === 'phone'">{{ item.phone || "" }}</template>
                                    <template v-else-if="column === 'address'">{{ item.address || "" }}</template>
                                    <template v-else-if="column === 'socials'">{{ socialsText(item.socials) }}</template>
                                    <template v-else-if="column === 'website'">{{ item.website || "" }}</template>
                                    <template v-else-if="column === 'category'">{{ categoryNames(item) }}</template>
                                    <template v-else-if="column === 'date'">{{ formatDate(item.created_at) }}</template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>

            <template v-else>
                <div class="row g-3">
                    <div
                        v-for="item in visibleItems"
                        :key="`card-${item.id}`"
                        :class="cardsPerRowClass"
                    >
                        <div class="card h-100">
                            <img
                                v-if="showColumn('image') && imageUrl(item)"
                                class="card-img-top"
                                :src="imageUrl(item)"
                                :alt="item.name || ''"
                                style="height: 190px; object-fit: cover"
                            />
                            <div class="card-body">
                                <h6 v-if="showColumn('name')" class="card-title mb-2">{{ item.name }}</h6>
                                <p v-if="showColumn('email') && item.email" class="card-text mb-1"><strong>Email:</strong> {{ item.email }}</p>
                                <p v-if="showColumn('phone') && item.phone" class="card-text mb-1"><strong>Phone:</strong> {{ item.phone }}</p>
                                <p v-if="showColumn('address') && item.address" class="card-text mb-1"><strong>Address:</strong> {{ item.address }}</p>
                                <p v-if="showColumn('socials') && socialsText(item.socials)" class="card-text mb-1"><strong>Socials:</strong> {{ socialsText(item.socials) }}</p>
                                <p v-if="showColumn('website') && item.website" class="card-text mb-1"><strong>Website:</strong> {{ item.website }}</p>
                                <p v-if="showColumn('category')" class="card-text mb-1"><strong>Categories:</strong> {{ categoryNames(item) }}</p>
                                <p v-if="showColumn('date')" class="card-text mb-0"><strong>Date:</strong> {{ formatDate(item.created_at) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <div class="text-center mt-3" v-if="showLoadMore && hasMoreItems">
                <button type="button" class="btn btn-outline-dark btn-sm" @click.stop="loadMore">Load more</button>
            </div>

            <p v-if="!visibleItems.length" class="text-muted mb-0">No business directory entries found.</p>
        </div>
    </div>
</template>

<script>
import cms6Mixin from "@/mixins/cms6Mixin";

export default {
    name: "BusinessDirectory",
    mixins: [cms6Mixin],
    props: {
        element: {
            required: true,
            type: Object,
        },
    },
    data: () => ({
        items: [],
        categories: [],
        activeTab: "all",
        visibleCount: 6,
    }),
    computed: {
        styleObject() {
            return {
                padding: this.element.style?.padding || "0px",
                margin: this.element.style?.margin || "0px",
            };
        },
        categoryMode() {
            return this.element.props.categoryMode || "all";
        },
        selectedCategoryId() {
            return this.element.props.categoryId || "";
        },
        viewMode() {
            return this.element.props.viewMode || "cards";
        },
        cardsPerRowClass() {
            const map = {
                "2": "col-12 col-md-6",
                "3": "col-12 col-md-4",
                "4": "col-12 col-md-3",
            };

            return map[String(this.element.props.cardsPerRow || "3")] || "col-12 col-md-4";
        },
        showTabs() {
            return this.categoryMode === "all" && this.element.props.showTabsWhenAll && this.categoryTabs.length > 2;
        },
        showLoadMore() {
            return !!this.element.props.showLoadMore;
        },
        showUncategorized() {
            return this.element.props.showUncategorized !== false;
        },
        availableItems() {
            if (this.categoryMode === "all" && !this.showUncategorized) {
                return this.items.filter((item) => this.itemCategoryIds(item).length);
            }

            return this.items;
        },
        filteredItems() {
            if (this.categoryMode !== "all" && this.selectedCategoryId) {
                return this.availableItems.filter((item) => this.itemHasCategory(item, this.selectedCategoryId));
            }

            if (this.categoryMode === "all" && this.showTabs && this.activeTab !== "all") {
                if (this.activeTab === "uncategorized") {
                    return this.showUncategorized ? this.availableItems.filter((item) => !this.itemCategoryIds(item).length) : [];
                }
                return this.availableItems.filter((item) => this.itemHasCategory(item, this.activeTab));
            }

            return this.availableItems;
        },
        visibleItems() {
            return this.filteredItems.slice(0, this.visibleCount);
        },
        hasMoreItems() {
            return this.filteredItems.length > this.visibleCount;
        },
        categoryTabs() {
            const tabs = [{ id: "all", name: "All" }];
            this.categories.forEach((category) => {
                tabs.push({ id: String(category.id), name: category.name });
            });

            if (this.showUncategorized && this.items.some((item) => !this.itemCategoryIds(item).length)) {
                tabs.push({ id: "uncategorized", name: "Uncategorized" });
            }

            return tabs;
        },
        initialCount() {
            const value = parseInt(this.element.props.initialCount || 6, 10);
            return Number.isNaN(value) ? 6 : value;
        },
        loadMoreStep() {
            const value = parseInt(this.element.props.loadMoreStep || 6, 10);
            return Number.isNaN(value) ? 6 : value;
        },
        visibleColumns() {
            const fallback = ["image", "name", "email", "phone", "address"];
            const allowed = ["image", "name", "email", "phone", "address", "socials", "website", "category", "date"];
            const selected = Array.isArray(this.element?.props?.visibleColumns) ? this.element.props.visibleColumns.filter((column) => allowed.includes(column)) : [];
            return selected.length ? selected : fallback;
        },
    },
    watch: {
        activeTab() {
            this.visibleCount = this.initialCount;
        },
        showUncategorized() {
            if (!this.showUncategorized && this.activeTab === "uncategorized") {
                this.activeTab = "all";
                return;
            }
            this.visibleCount = this.initialCount;
        },
        selectedCategoryId() {
            this.visibleCount = this.initialCount;
        },
        "element.props.initialCount"() {
            this.visibleCount = this.initialCount;
        },
    },
    async created() {
        await this.fetchData();
        this.visibleCount = this.initialCount;
    },
    methods: {
        onClick() {
            this.$emit("onClick", this.element);
        },
        async fetchData() {
            if (typeof this.getBusinessDirectories !== "function") {
                return;
            }

            const directoriesRes = await this.getBusinessDirectories('pb-business-directory-items');
            const categoriesRes = await this.getBusinessDirectoryCategories('pb-business-directory-categories');

            this.items = directoriesRes?.data || [];
            this.categories = categoriesRes?.data || [];
        },
        loadMore() {
            this.visibleCount += this.loadMoreStep;
        },
        showColumn(column) {
            return this.visibleColumns.includes(column);
        },
        columnLabel(column) {
            const labels = {
                image: "Image",
                name: "Name",
                email: "Email",
                phone: "Phone",
                address: "Address",
                socials: "Socials",
                website: "Website",
                category: "Categories",
                date: "Date",
            };
            return labels[column] || column;
        },
        socialsText(socials) {
            if (!Array.isArray(socials)) {
                return "";
            }
            return socials
                .map((item) => {
                    const title = (item?.title || "").trim();
                    const value = (item?.value || "").trim();
                    if (title && value) return `${title}: ${value}`;
                    return title || value;
                })
                .filter(Boolean)
                .join(" | ");
        },
        imageUrl(item) {
            if (item?.featured_media?.source_url) {
                return item.featured_media.source_url;
            }
            if (item?.featured_image?.source_url) {
                return item.featured_image.source_url;
            }
            if (item?.featured_image?.media_details?.sizes?.thumbnail?.source_url) {
                return item.featured_image.media_details.sizes.thumbnail.source_url;
            }
            if (item?.featuredImage?.source_url) {
                return item.featuredImage.source_url;
            }
            return null;
        },
        itemCategoryIds(item) {
            const rawIds = Array.isArray(item?.categories)
                ? item.categories.map((category) => (typeof category === "object" ? category?.id : category))
                : Array.isArray(item?.category_ids)
                  ? item.category_ids
                  : item?.business_category_id
                    ? [item.business_category_id]
                    : [];

            return rawIds
                .map((categoryId) => String(categoryId))
                .filter((categoryId, index, ids) => categoryId && ids.indexOf(categoryId) === index);
        },
        itemHasCategory(item, categoryId) {
            return this.itemCategoryIds(item).includes(String(categoryId));
        },
        categoryNames(item) {
            const names = this.itemCategoryIds(item)
                .map((categoryId) => this.categories.find((category) => String(category.id) === String(categoryId))?.name)
                .filter(Boolean);

            return names.length ? names.join(", ") : "Uncategorized";
        },
        formatDate(date) {
            if (!date) {
                return "";
            }
            return new Date(date).toISOString().slice(0, 10);
        },
    },
    emits: ["onClick"],
};
</script>
